import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { logout } from "@/api/auth";
import { useAuthStatus, useClearAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";

/**
 * Layout commun avec header (logo, navigation, bouton de connexion/déconnexion).
 * Le bandeau "estimation sans valeur légale" est rappelé en permanence.
 */
export function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="lg-shell flex min-h-screen flex-col text-gray-900 dark:text-gray-100">
      <Header />
      <DisclaimerBanner />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function ThemeToggle() {
  const { mode, setTheme } = useTheme();
  // Cycle : light → dark → system → light…
  const next = mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
  const labels: Record<typeof mode, string> = {
    light: "☀️ Clair",
    dark: "🌙 Sombre",
    system: "🖥️ Auto",
  };
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      title={`Thème : ${labels[mode]}. Cliquer pour basculer.`}
      aria-label={`Changer le thème (actuel : ${labels[mode]})`}
      className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800"
    >
      {labels[mode]}
    </button>
  );
}

function Header() {
  const { isAuthenticated, user } = useAuthStatus();
  const clearAuth = useClearAuth();
  const navigate = useNavigate();

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      clearAuth();
      navigate("/", { replace: true });
    },
  });

  return (
    <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="lg-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M12 21c5-2 8-6 8-11V5l-8-2-8 2v5c0 5 3 9 8 11Z"
                stroke="#fff"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="text-lg font-bold tracking-tight">Mon Diag Énergétique</span>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="hover:underline">
                Mes logements
              </Link>
              {user?.is_admin && (
                <Link to="/admin" className="font-medium text-blue-900 hover:underline dark:text-blue-300">
                  Admin
                </Link>
              )}
              <Link to="/account" className="hover:underline">
                {user?.email}
              </Link>
              <button
                type="button"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
                className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                Se déconnecter
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hover:underline">
                Se connecter
              </Link>
              <Link
                to="/register"
                className="rounded bg-blue-900 px-3 py-1 text-white hover:bg-blue-800 dark:bg-blue-700 dark:hover:bg-blue-600"
              >
                S&apos;inscrire
              </Link>
            </>
          )}
          <ThemeToggle />
        </nav>
      </div>
    </header>
  );
}

function DisclaimerBanner() {
  return (
    <div
      role="note"
      className="border-b border-amber-300 bg-amber-50 px-6 py-2 text-center text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200"
    >
      Estimation pédagogique inspirée de la méthode 3CL-DPE 2021.{" "}
      <strong>Sans valeur légale.</strong> Pour un DPE officiel, faire appel à un
      diagnostiqueur certifié (annuaire public).
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="mx-auto max-w-5xl px-6 py-6 text-xs text-gray-500 dark:text-gray-400">
        © {new Date().getFullYear()} Mon Diag Énergétique · Estimation pédagogique, sans valeur légale.
        <br />
        <span className="text-[11px]">
          <Link to="/methodologie" className="hover:underline">Méthodologie</Link>
          {" · "}
          <Link to="/mentions-legales" className="hover:underline">Mentions légales</Link>
          {" · "}
          <Link to="/cgu" className="hover:underline">CGU</Link>
          {" · "}
          <Link to="/confidentialite" className="hover:underline">Politique de confidentialité</Link>
          {" · "}
          <Link to="/cookies" className="hover:underline">Gestion des cookies</Link>
        </span>
      </div>
    </footer>
  );
}
